import { toViewableImageUrl } from './catalogProductImageStorage';
import { getCatalogOwnGroup } from './catalogContentSignature';
import { cardBelongsToCatalogGroup } from './accessUtils';
import {
  cardBelongsToProductAccess,
  normalizeProductAccessIds,
} from '../pages/AdminPanel/Products/service/product-catalog-filters-service';
import {
  formatContentTypeLabel,
  getCardCoverUrl,
  getVideoCoverUrl,
} from './coverImageAiPromptShared';

const MAX_SIBLING_COVERS = 15;
const MAX_MODULE_COVERS = 20;

export function extractSiblingModuleCovers(cards = [], { excludeCardId, orientation } = {}) {
  const covers = cards
    .filter((card) => card.id !== excludeCardId)
    .map((card) => {
      const url = getCardCoverUrl(card, orientation);
      if (!url) return null;

      return {
        moduleTitle: card.title || '(sem título)',
        url,
      };
    })
    .filter(Boolean);

  if (covers.length <= MAX_SIBLING_COVERS) {
    return { items: covers, omittedCount: 0 };
  }

  return {
    items: covers.slice(0, MAX_SIBLING_COVERS),
    omittedCount: covers.length - MAX_SIBLING_COVERS,
  };
}

export function extractSiblingContentCovers(videos = [], { excludeVideoId } = {}) {
  const covers = videos
    .filter((video) => video.docId !== excludeVideoId)
    .map((video) => {
      const url = getVideoCoverUrl(video);
      if (!url) return null;

      return {
        contentTitle: video.title || '(sem título)',
        contentType: formatContentTypeLabel(video.type),
        url,
      };
    })
    .filter(Boolean);

  if (covers.length <= MAX_SIBLING_COVERS) {
    return { items: covers, omittedCount: 0 };
  }

  return {
    items: covers.slice(0, MAX_SIBLING_COVERS),
    omittedCount: covers.length - MAX_SIBLING_COVERS,
  };
}

export function buildModuleCoverReferenceOptions({
  siblingCards = [],
  excludeCardId = '',
  orientation = 'horizontal',
} = {}) {
  const { items } = extractSiblingModuleCovers(siblingCards, { excludeCardId, orientation });

  return items.map((cover) => ({
    id: cover.url,
    label: cover.moduleTitle,
    subtitle: 'Módulo na categoria',
    url: cover.url,
    kind: 'sibling-module',
  }));
}

export function buildContentCoverReferenceOptions({
  videos = [],
  excludeVideoId = '',
  moduleTitle = '',
  moduleCoverUrl = null,
} = {}) {
  const { items } = extractSiblingContentCovers(videos, { excludeVideoId });
  const options = items.map((cover) => ({
    id: cover.url,
    label: cover.contentTitle,
    subtitle: cover.contentType,
    url: cover.url,
    kind: 'sibling-content',
  }));

  if (moduleCoverUrl) {
    options.push({
      id: moduleCoverUrl,
      label: moduleTitle || 'Capa do módulo',
      subtitle: 'Capa do módulo',
      url: moduleCoverUrl,
      kind: 'module-cover',
    });
  }

  return options;
}

/**
 * Capas de módulos vinculados a este produto/plano/curso (catálogo interno).
 * Usa a mesma regra de vínculo que equivalentes e filtros do catálogo.
 */
export function buildCatalogModuleCoverReferenceOptions(
  categories = [],
  { selfProduct = null, accessIds = [] } = {}
) {
  const group = getCatalogOwnGroup(selfProduct);
  const normalizedAccessIds = normalizeProductAccessIds(
    accessIds?.length ? accessIds : selfProduct?.accessIds || selfProduct?.ownIds || []
  );

  if (!categories.length) return [];
  if (!group.length && !normalizedAccessIds.length) return [];

  const seenUrls = new Set();
  const options = [];

  categories.forEach((category) => {
    (category.cards || []).forEach((card) => {
      const belongs = group.length
        ? cardBelongsToCatalogGroup(card, group)
        : cardBelongsToProductAccess(card, normalizedAccessIds);

      if (!belongs) return;

      const url = getCardCoverUrl(card);
      if (!url || seenUrls.has(url)) return;
      seenUrls.add(url);

      options.push({
        id: url,
        label: card.title || '(sem título)',
        subtitle: `Módulo do catálogo — ${category.title || '(sem categoria)'}`,
        url,
        kind: 'catalog-module',
        categoryTitle: category.title || '(sem categoria)',
      });
    });
  });

  return options
    .sort((a, b) => {
      const categoryCompare = (a.categoryTitle || '').localeCompare(b.categoryTitle || '', 'pt-BR');
      if (categoryCompare !== 0) return categoryCompare;
      return a.label.localeCompare(b.label, 'pt-BR');
    })
    .slice(0, MAX_MODULE_COVERS);
}

/**
 * Capas de outros produtos na vitrine — opções para o picker de referência.
 */
export function buildProductCoverReferenceOptions({
  catalogProducts = [],
  excludeProductId = '',
  equivalentIds = [],
  productCategoryId = null,
  productCategories = [],
} = {}) {
  const excludeIds = new Set([excludeProductId, ...(equivalentIds || [])]);
  const categoryNameById = new Map(productCategories.map((category) => [category.id, category.name]));
  const seenUrls = new Set();

  const options = catalogProducts
    .filter((product) => !excludeIds.has(product.id))
    .map((product) => {
      const rawUrl = product?.catalogImage?.url;
      const url = rawUrl ? toViewableImageUrl(rawUrl) : null;
      if (!url || seenUrls.has(url)) return null;
      seenUrls.add(url);

      const sameCategory = productCategoryId && product.productCategoryId === productCategoryId;
      const categoryName = product.productCategoryId
        ? categoryNameById.get(product.productCategoryId)
        : null;

      return {
        id: url,
        label: product.name || '(sem nome)',
        subtitle: sameCategory
          ? `Mesma categoria${categoryName ? `: ${categoryName}` : ''}`
          : categoryName
            ? `Categoria: ${categoryName}`
            : 'Outro produto na vitrine',
        url,
        kind: 'sibling-product',
        sameCategory,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.sameCategory !== b.sameCategory) return a.sameCategory ? -1 : 1;
      return a.label.localeCompare(b.label, 'pt-BR');
    });

  if (options.length <= MAX_PRODUCT_COVERS) {
    return options;
  }

  return options.slice(0, MAX_PRODUCT_COVERS);
}

/** Mescla capas de módulos do catálogo do produto + capas de outros produtos na vitrine. */
export function mergeProductCoverReferenceOptions(moduleOptions = [], productOptions = []) {
  const seenUrls = new Set();
  const merged = [];

  for (const option of [...moduleOptions, ...productOptions]) {
    if (!option?.url || seenUrls.has(option.url)) continue;
    seenUrls.add(option.url);
    merged.push(option);
  }

  return merged;
}
